import type { CookSkill } from "../api/cook";
import { ItemSprite } from "./ItemSprite";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";

interface Props {
  skill: CookSkill | null;
  sessionXp: number;
  // item_id → count, for the session-cooked tally.
  sessionCooked: Map<string, number>;
  // item_id → human name, looked up from the inventory catalog so
  // we don't have to hardcode "Baked Apple" etc.
  itemNames: Map<string, string>;
}

export function CookSidePanel({
  skill,
  sessionXp,
  sessionCooked,
  itemNames,
}: Props) {
  const theme = pixelTheme;
  const xpPct =
    skill && skill.xp_to_next > 0
      ? Math.max(
          0,
          Math.min(100, (skill.xp_into_level / skill.xp_to_next) * 100),
        )
      : 0;

  const entries = Array.from(sessionCooked.entries()).filter(
    ([, c]) => c > 0,
  );

  return (
    <aside style={{ display: "grid", gap: 16 }}>
      <div style={{ ...simplePanelStyle(theme), padding: "14px 16px" }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.16em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          SKILL
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginTop: 6,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: "0.08em",
              color: theme.ink,
              textTransform: "uppercase",
            }}
          >
            COOKING
          </div>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 28,
              letterSpacing: "0.06em",
              color: theme.accent,
            }}
          >
            {skill?.level ?? 1}
          </div>
        </div>
        <div
          title={`${skill?.xp_into_level ?? 0} / ${skill?.xp_to_next ?? 0} XP`}
          style={{
            position: "relative",
            marginTop: 10,
            height: 14,
            background: E32.plum,
            border: `2px solid ${theme.ink}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              right: "auto",
              width: `${xpPct}%`,
              background: E32.forest,
              transition: "width 240ms ease-out",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: theme.inkDim,
            letterSpacing: "0.08em",
          }}
        >
          <span>{(skill?.xp ?? 0).toLocaleString()} XP</span>
          <span>NEXT · {(skill?.xp_to_next ?? 0).toLocaleString()}</span>
        </div>
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: `1px dashed ${theme.panelEdge}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              letterSpacing: "0.16em",
              color: theme.inkFaint,
              textTransform: "uppercase",
            }}
          >
            THIS SESSION
          </span>
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 16,
              color: E32.forest,
              letterSpacing: "0.06em",
            }}
          >
            +{sessionXp} XP
          </span>
        </div>
      </div>

      <div style={{ ...simplePanelStyle(theme), padding: "14px 16px" }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.16em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          SESSION COOKED
        </div>
        {entries.length === 0 ? (
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: 16,
              color: theme.inkFaint,
              padding: "8px 4px",
            }}
          >
            Nothing yet.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
            {entries.map(([itemId, count]) => (
              <div
                key={itemId}
                style={{
                  display: "grid",
                  gridTemplateColumns: "32px 1fr auto",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 8px",
                  background: E32.parchment,
                  border: `2px solid ${theme.panelEdge}`,
                }}
              >
                <ItemSprite id={itemId} size={24} />
                <div
                  style={{
                    fontFamily: theme.fontBody,
                    fontSize: 16,
                    color: theme.ink,
                    lineHeight: 1,
                  }}
                >
                  {itemNames.get(itemId) ?? itemId}
                </div>
                <div
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 11,
                    color: theme.ink,
                    letterSpacing: "0.06em",
                  }}
                >
                  × {count}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
